import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ImageUploadField, PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Select, Textarea } from 'components/common';
import {
    createEcosystemService,
    getAdminEcosystemServiceById,
    updateEcosystemService,
} from 'apis/adminEcosystemServices';
import { getAdminEcosystemGroups } from 'apis/adminEcosystemGroups';
import {
    useActiveLanguagesQuery,
    useAdminCreateMutation,
    useAdminDetailQuery,
    useAdminListQuery,
    useAdminUpdateMutation,
} from 'hooks';
import '../../../styles/admin-shared.scss';

const initial = {
    ecosystemGroupId: '',
    code: '',
    serviceType: 'custom',
    serviceId: null,
    imageUrl: '',
    detailUrl: '',
    sortOrder: 0,
    isActive: true,
    translation: [],
};

const Form = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [form, setForm] = useState(initial);
    const [tab, setTab] = useState(0);

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: groupsData = { items: [] } } = useAdminListQuery(
        'ecosystem-groups-options',
        { pageSize: 100, languageCode: 'vi' },
        getAdminEcosystemGroups,
    );
    const groups = groupsData.items || [];

    const { data: detail } = useAdminDetailQuery(
        'ecosystem-services',
        id,
        getAdminEcosystemServiceById,
    );
    const create = useAdminCreateMutation('ecosystem-services', createEcosystemService);
    const update = useAdminUpdateMutation('ecosystem-services', updateEcosystemService);

    useEffect(() => {
        if (!id && languages.length) {
            setForm((p) => ({
                ...p,
                translation: languages.map((l) => ({
                    languageId: l.id,
                    title: '',
                    slug: '',
                    description: '',
                })),
            }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail) {
            setForm({
                ...initial,
                ...detail,
                ecosystemGroupId: detail.ecosystemGroupId || '',
                translation: detail.translation || [],
            });
        }
    }, [detail]);

    const change = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((p) => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
    };

    const changeT = (languageId, field, value) => {
        setForm((p) => ({
            ...p,
            translation: p.translation.map((t) =>
                t.languageId === languageId ? { ...t, [field]: value } : t,
            ),
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        const vi =
            form.translation.find(
                (t) => t.languageId === languages.find((l) => l.code === 'vi')?.id,
            ) ||
            form.translation[0] ||
            {};
        const generatedCode = form.code || `es-${Date.now()}`;
        const payload = {
            ...form,
            code: generatedCode,
            ecosystemGroupId: form.ecosystemGroupId,
            title: vi.title || '',
            description: vi.description || '',
            sortOrder: Number(form.sortOrder) || 0,
        };

        try {
            const res = id
                ? await update.mutateAsync({ id, data: payload })
                : await create.mutateAsync(payload);
            if (!res?.success) throw new Error(res?.message);
            toast.success('Đã lưu dịch vụ hệ sinh thái');
            navigate('/admin/ecosystem-services');
        } catch (err) {
            toast.error(err.message || 'Lưu thất bại');
        }
    };

    const groupOptions = [
        { value: '', label: 'Chọn nhóm hệ sinh thái...' },
        ...groups.map((g) => ({
            value: g.id,
            label: g.name || g.code,
        })),
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title={id ? 'Cập nhật Dịch vụ Hệ sinh thái' : 'Thêm Dịch vụ Hệ sinh thái'}
            />
            <form className="form-card" onSubmit={submit}>
                <p className="form-section-title">Cài đặt</p>
                <div className="form-row">
                    <Select
                        label="Nhóm hệ sinh thái *"
                        name="ecosystemGroupId"
                        value={form.ecosystemGroupId}
                        onChange={change}
                        options={groupOptions}
                        required
                    />
                    <Input
                        label="Thứ tự sắp xếp"
                        type="number"
                        name="sortOrder"
                        value={form.sortOrder}
                        onChange={change}
                    />
                </div>
                <div className="form-row">
                    <ImageUploadField
                        label="Hình ảnh dịch vụ"
                        value={form.imageUrl}
                        folder="ecosystem-services"
                        onChange={(imageUrl) => setForm(prev => ({ ...prev, imageUrl }))}
                    />
                    <Input
                        label="URL chi tiết (tùy chọn)"
                        name="detailUrl"
                        value={form.detailUrl || ''}
                        onChange={change}
                        placeholder="/..."
                    />
                </div>
                <Checkbox
                    label="Hoạt động"
                    name="isActive"
                    checked={form.isActive}
                    onChange={change}
                />

                <p className="form-section-title">Bản dịch</p>
                <div className="translation-tabs">
                    <div className="tab-header">
                        {languages.map((l, i) => (
                            <button
                                key={l.id}
                                type="button"
                                className={`tab-btn ${tab === i ? 'active' : ''}`}
                                onClick={() => setTab(i)}
                            >
                                {l.name}
                            </button>
                        ))}
                    </div>
                    {languages.map((l, i) => {
                        if (i !== tab) return null;
                        const t =
                            form.translation.find((x) => x.languageId === l.id) || {};
                        return (
                            <div className="tab-content" key={l.id}>
                                <div className="form-row">
                                    <Input
                                        label="Tên dịch vụ *"
                                        value={t.title || ''}
                                        onChange={(e) =>
                                            changeT(l.id, 'title', e.target.value)
                                        }
                                        required
                                    />
                                    <Input
                                        label="Slug (tùy chọn)"
                                        value={t.slug || ''}
                                        onChange={(e) => changeT(l.id, 'slug', e.target.value)}
                                    />
                                </div>
                                <Textarea
                                    label="Mô tả dịch vụ"
                                    rows={4}
                                    value={t.description || ''}
                                    onChange={(e) =>
                                        changeT(l.id, 'description', e.target.value)
                                    }
                                />
                            </div>
                        );
                    })}
                </div>

                <div className="form-actions">
                    <Button type="submit" disabled={create.isPending || update.isPending}>
                        Lưu dịch vụ
                    </Button>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => navigate('/admin/ecosystem-services')}
                    >
                        Hủy
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default Form;
